import { logger } from "@/utils/logger";
import { getSelectedBeta, getAnalysisData, getOptimizationData, hasAnalysisData, } from "./analysis.bridge";
import { getFleetStore } from "../fleet/fleet.getters.bridge";
import { createMethodPlan, syncServiceBitis } from "./planBuilders";
import { createEmptyServicePlansByMethod, } from "./plans.state";
import frequencyApi from "@/api/frequency.api";
import { servicesHaveAracIds } from "../fleet/assignVehicles.algorithm";
import { useBulkSessionStore } from "@/stores/bulkSession.store";
function collectMethodServices(plan) {
    if (!plan)
        return [];
    return [...(plan.gidis || []), ...(plan.donus || [])].map((service) => ({
        ...service,
        arac_tipi: service.arac_tipi || service.original_arac_tipi || "SOLO",
    }));
}
/** Optimizasyonla aynı flex; gönderilmezse path-cover flex kapalı kalıyordu. */
function resolveTripFlexForPathCover(hatNo) {
    try {
        const bulk = useBulkSessionStore();
        const key = String(hatNo || "").trim();
        const eff = (key && typeof bulk.effectiveParams === "function"
            ? bulk.effectiveParams(key)
            : null) ||
            bulk.globalParams ||
            {};
        const maxFlex = Number(eff.maxFlexMinutes ?? 3);
        return {
            enabled: eff.tripFlexEnabled !== false,
            maxFlexMinutes: Number.isFinite(maxFlex) ? maxFlex : 3,
        };
    }
    catch {
        return { enabled: true, maxFlexMinutes: 3 };
    }
}
function applyAracIdsToActivePlan(store, methodKey, atamalar) {
    const plan = store.activePlans[methodKey];
    if (!plan || !Array.isArray(atamalar))
        return;
    const byId = new Map(atamalar
        .filter((a) => a?.id != null)
        .map((a) => [String(a.id), a]));
    for (const yon of ["gidis", "donus"]) {
        const list = plan[yon] || [];
        for (let i = 0; i < list.length; i++) {
            const svc = list[i];
            const hit = byId.get(String(svc.id));
            if (!hit)
                continue;
            list[i] = {
                ...svc,
                arac_id: hit.arac_id,
                bitis: hit.bitis || svc.bitis,
            };
        }
    }
}
/** Plan üretimi, persistence, method değişimi ve araç atama aksiyonları */
export const frequencyPlanActions = {
    /** Vuex: changeMethod */
    changeMethod({ methodNumber, noVehicleAssignment = false, selectedBeta, } = {}) {
        let methodKey;
        if (methodNumber === 4) {
            const betaValue = getSelectedBeta(selectedBeta);
            const betaPercentage = Math.round(betaValue * 100);
            if ([0, 10, 20, 30].includes(betaPercentage)) {
                methodKey = `method4_${betaPercentage}`;
            }
            else {
                logger.warn(`Geçersiz beta değeri: ${betaValue}, aşım yok (0) kullanılıyor`);
                methodKey = "method4_0";
            }
        }
        else {
            methodKey = `method${methodNumber}`;
        }
        logger.debug(`Method değişiyor: ${methodNumber}, key: ${methodKey}`);
        this.applyUserModifications(methodKey);
        if (!noVehicleAssignment) {
            const allServices = [
                ...this.activePlans[methodKey].gidis,
                ...this.activePlans[methodKey].donus,
            ];
            logger.debug(`${methodKey} için araç planı güncellenecek (${allServices.length} servis)`);
            logger.debug("Araç tipleri dağılımı:", allServices.reduce((acc, s) => {
                acc[s.arac_tipi] = (acc[s.arac_tipi] || 0) + 1;
                return acc;
            }, {}));
            return this.updateVehiclePlan(methodKey);
        }
    },
    /** Vuex: applyUserModifications */
    applyUserModifications(methodKey) {
        const newPlan = JSON.parse(JSON.stringify(this.basePlans[methodKey]));
        const userMods = this.userModifications[methodKey];
        Object.entries(userMods.parkurSureleri).forEach(([parkurKey, sure]) => {
            const parts = parkurKey.split("_");
            const saat = parseInt(parts[0], 10);
            const yon = parts[parts.length - 1];
            const etkilenenServisler = newPlan[yon].filter((servis) => parseInt(servis.baslangic.split(":")[0], 10) === saat);
            etkilenenServisler.forEach((servis) => {
                servis.sure = sure;
            });
        });
        userMods.removedServices.forEach((serviceId) => {
            const yon = serviceId.startsWith("gidis") ? "gidis" : "donus";
            const index = newPlan[yon].findIndex((s) => s.id === serviceId);
            if (index !== -1) {
                newPlan[yon].splice(index, 1);
            }
        });
        userMods.modifiedServices.forEach((modifiedService) => {
            const yon = modifiedService.yon;
            const index = newPlan[yon].findIndex((s) => s.id === modifiedService.id);
            if (index !== -1) {
                newPlan[yon][index] = { ...modifiedService };
            }
        });
        userMods.addedServices.forEach((service) => {
            newPlan[service.yon].push({ ...service });
        });
        this.updateActivePlan({ methodKey, plan: newPlan });
        return this.updateVehiclePlan(methodKey);
    },
    /** Vuex: updateVehiclePlan — path-cover arac_id öncelikli; dirty ise backend path-cover */
    updateVehiclePlan(methodKey, options = {}) {
        const forcePathCover = !!options.forcePathCover;
        let allServices = collectMethodServices(this.activePlans[methodKey]);
        const aracTipiDagilimi = allServices.reduce((acc, s) => {
            acc[s.arac_tipi] = (acc[s.arac_tipi] || 0) + 1;
            return acc;
        }, {});
        logger.debug(`${methodKey} için araç planı güncelleniyor:`, aracTipiDagilimi, { forcePathCover });
        if (allServices.length === 0) {
            this.setVehiclePlan({ methodKey, plan: [] });
            this.clearVehiclePathCoverDirty(methodKey);
            return Promise.resolve({ vehicles: [], assignments: [] });
        }
        // dirty yalnız banner için; path-cover yalnızca forcePathCover veya arac_id yoksa.
        // (Arka plan sync dirty'yi tüketip uyarıyı kaçırmasın — workspace'te iki panel açık.)
        const needPathCover = forcePathCover || !servicesHaveAracIds(allServices);
        const runAssign = (services) => getFleetStore()
            .assignVehicles({ services, mode: "fromAracId" })
            .then((result) => {
            if (result && result.vehicles) {
                const vehiclesWithAssignments = result.vehicles.map((v) => ({
                    ...v,
                    assignments: (result.assignments || []).filter((a) => a.arac_id === v.id) ||
                        [],
                }));
                this.setVehiclePlan({
                    methodKey,
                    plan: vehiclesWithAssignments,
                });
                if (needPathCover) {
                    this.clearVehiclePathCoverDirty(methodKey);
                }
                logger.debug(`${methodKey} için araç planı güncellendi (${result.vehicles.length} araç, path-cover)`);
                window.dispatchEvent(new CustomEvent("vehicle-plan-updated", {
                    detail: {
                        methodKey,
                        vehicles: result.vehicles,
                        assignments: result.assignments,
                    },
                }));
                return result;
            }
            logger.error("Araç atama sonuçları geçersiz:", result);
            return { vehicles: [], assignments: [] };
        });
        if (!needPathCover) {
            return runAssign(allServices).catch((error) => {
                logger.error("Araç atama hatası:", error);
                return { vehicles: [], assignments: [] };
            });
        }
        const analysis = getAnalysisData() || {};
        const opt = getOptimizationData() || {};
        const hatNo = analysis.hat_no ||
            analysis.hatNo ||
            opt.hat_no ||
            opt.hatNo ||
            "0";
        const payloadSeferler = allServices.map((s) => {
            const synced = syncServiceBitis(s);
            return {
                id: synced.id,
                baslangic: synced.baslangic,
                bitis: synced.bitis,
                sure: synced.sure,
                yon: synced.yon,
                arac_tipi: synced.arac_tipi || "SOLO",
            };
        });
        const tripFlex = resolveTripFlexForPathCover(hatNo);
        return frequencyApi
            .pathCoverAtama({
            hatNo,
            seferler: payloadSeferler,
            tripFlex,
        })
            .then((res) => {
            if (!res?.success || !Array.isArray(res.atamalar)) {
                throw new Error(res?.error || "Path-cover atama başarısız");
            }
            applyAracIdsToActivePlan(this, methodKey, res.atamalar);
            allServices = collectMethodServices(this.activePlans[methodKey]);
            return runAssign(allServices);
        })
            .catch((error) => {
            logger.error("Path-cover araç atama hatası:", error);
            // Son çare: mevcut arac_id varsa onları kullan, yoksa greedy
            const mode = servicesHaveAracIds(allServices) ? "fromAracId" : "greedy";
            return getFleetStore()
                .assignVehicles({ services: allServices, mode })
                .then((result) => {
                if (result?.vehicles) {
                    this.setVehiclePlan({
                        methodKey,
                        plan: result.vehicles.map((v) => ({
                            ...v,
                            assignments: (result.assignments || []).filter((a) => a.arac_id === v.id) || [],
                        })),
                    });
                    window.dispatchEvent(new CustomEvent("vehicle-plan-updated", {
                        detail: {
                            methodKey,
                            vehicles: result.vehicles,
                            assignments: result.assignments,
                        },
                    }));
                }
                return result || { vehicles: [], assignments: [] };
            });
        });
    },
    markVehiclePathCoverDirty(methodKey) {
        if (!methodKey)
            return;
        this.vehiclePathCoverDirty = {
            ...(this.vehiclePathCoverDirty || {}),
            [methodKey]: true,
        };
    },
    clearVehiclePathCoverDirty(methodKey) {
        if (!methodKey || !this.vehiclePathCoverDirty)
            return;
        this.vehiclePathCoverDirty = {
            ...this.vehiclePathCoverDirty,
            [methodKey]: false,
        };
    },
    isVehiclePathCoverDirty(methodKey) {
        return !!this.vehiclePathCoverDirty?.[methodKey];
    },
    /** Vuex: resetPlans */
    resetPlans() {
        this.setBasePlans(createEmptyServicePlansByMethod());
        Object.keys(this.vehiclePlans).forEach((methodKey) => {
            this.setVehiclePlan({ methodKey, plan: [] });
        });
    },
    /** Vuex: updateActivePlans */
    updateActivePlans({ methodKey }) {
        if (!methodKey)
            return;
        logger.debug(`Aktif planlar güncelleniyor: ${methodKey}`);
        const updatedPlan = {
            ...this.activePlans[methodKey],
            _lastUpdated: Date.now(),
        };
        this.updateActivePlan({ methodKey, plan: updatedPlan });
        return this.saveStateToPersistence();
    },
    /** Vuex: saveStateToPersistence */
    saveStateToPersistence() {
        try {
            const persistData = {
                activePlans: this.activePlans,
                vehiclePlans: this.vehiclePlans,
                lastSaved: Date.now(),
            };
            localStorage.setItem("planStoreData", JSON.stringify(persistData));
            logger.debug("Plan verileri localStorage'a kaydedildi");
            return true;
        }
        catch (error) {
            logger.error("Plan verilerini kaydetme hatası:", error);
            return false;
        }
    },
    /** Vuex: generateBasePlan — tek methodKey için base→active veya createMethodPlan */
    generateBasePlan(methodKey) {
        const base = this.basePlans[methodKey];
        const hasBase = base &&
            ((base.gidis?.length ?? 0) > 0 || (base.donus?.length ?? 0) > 0);
        if (hasBase) {
            this.updateActivePlan({
                methodKey,
                plan: JSON.parse(JSON.stringify(base)),
            });
            return Promise.resolve(this.applyUserModifications(methodKey));
        }
        const optimizationData = getOptimizationData();
        const analysisData = getAnalysisData();
        if (!optimizationData || !analysisData) {
            logger.warn(`${methodKey} için plan oluşturulamadı: analiz/optimizasyon yok`);
            return Promise.resolve(null);
        }
        const plan = createMethodPlan(optimizationData, methodKey, analysisData);
        this.basePlans[methodKey] = plan;
        this.updateActivePlan({
            methodKey,
            plan: JSON.parse(JSON.stringify(plan)),
        });
        return Promise.resolve(this.applyUserModifications(methodKey));
    },
    /** Vuex: generateAllBasePlans — analysisData vuexStore.state köprüsü */
    generateAllBasePlans(optimizationData) {
        logger.debug("Tüm temel planlar oluşturuluyor, eski veriler tamamen temizleniyor");
        return new Promise((resolve) => {
            const startTime = Date.now();
            const analysisData = getAnalysisData();
            this.setBasePlans(createEmptyServicePlansByMethod());
            this.resetUserModifications();
            setTimeout(() => {
                const basePlans = {};
                ["method1", "method5", "method6"].forEach((methodKey) => {
                    basePlans[methodKey] = createMethodPlan(optimizationData, methodKey, analysisData);
                });
                setTimeout(() => {
                    ["method2", "method3"].forEach((methodKey) => {
                        basePlans[methodKey] = createMethodPlan(optimizationData, methodKey, analysisData);
                    });
                    setTimeout(() => {
                        ["method4_10", "method4_20", "method4_30"].forEach((methodKey) => {
                            basePlans[methodKey] = createMethodPlan(optimizationData, methodKey, analysisData);
                        });
                        this.setBasePlans(basePlans);
                        const allMethodKeys = [
                            "method1",
                            "method2",
                            "method3",
                            "method4_10",
                            "method4_20",
                            "method4_30",
                            "method5",
                            "method6",
                        ];
                        allMethodKeys.forEach((mk) => {
                            this.updateActivePlan({
                                methodKey: mk,
                                plan: JSON.parse(JSON.stringify(basePlans[mk] || { gidis: [], donus: [] })),
                            });
                            this.clearVehiclePathCoverDirty(mk);
                        });
                        let chain = Promise.resolve();
                        allMethodKeys.forEach((mk) => {
                            chain = chain.then(() => this.updateVehiclePlan(mk).catch((err) => {
                                logger.warn(`${mk} araç planı oluşturulamadı:`, err);
                                return null;
                            }));
                        });
                        chain.then(() => {
                            this.resetServiceVehicleTypes();
                            logger.debug(`Plan oluşturma tamamlandı (${Date.now() - startTime}ms)`);
                            resolve(basePlans);
                        });
                    }, 50);
                }, 50);
            }, 50);
        });
    },
    /** Vuex: loadFromPersistence — analysisData vuexStore.state köprüsü */
    loadFromPersistence() {
        if (!hasAnalysisData()) {
            logger.debug("Analiz verisi olmadığı için localStorage'dan yükleme yapılmıyor");
            return Promise.resolve(false);
        }
        try {
            const savedData = localStorage.getItem("planStoreData");
            if (!savedData) {
                return Promise.resolve(false);
            }
            const parsedData = JSON.parse(savedData);
            if (parsedData.activePlans) {
                Object.entries(parsedData.activePlans).forEach(([methodKey, plan]) => {
                    this.updateActivePlan({ methodKey, plan });
                });
            }
            if (parsedData.vehiclePlans) {
                Object.entries(parsedData.vehiclePlans).forEach(([methodKey, plan]) => {
                    this.setVehiclePlan({ methodKey, plan });
                });
            }
            logger.debug("Plan verileri localStorage'dan yüklendi");
            return Promise.resolve(true);
        }
        catch (error) {
            logger.error("Plan verilerini yükleme hatası:", error);
            return Promise.resolve(false);
        }
    },
    /** Vuex: resetPlansAndTypes */
    resetPlansAndTypes() {
        this.resetPlans();
        this.resetServiceVehicleTypes();
    },
};
